import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound, Loader2 } from "lucide-react";

export default function AdminChangePasswordPage() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setSaving(true);
    try {
      await api.changePassword(form.current_password, form.new_password);
      toast.success("Contraseña actualizada");
      await refreshMe();
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={ATID.changePwPage} className="max-w-md">
      <header className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.28em] text-textgray">Panel</div>
        <h1 className="text-3xl font-black text-nightblue mt-1 font-serif flex items-center gap-2">
          <KeyRound className="w-6 h-6" /> Cambiar contraseña
        </h1>
        {user?.must_change_password && (
          <p className="text-sm text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-3">
            Por seguridad, tenés que definir una nueva contraseña antes de continuar.
          </p>
        )}
      </header>

      <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-softblue rounded-md p-6">
        <div>
          <Label>Contraseña actual</Label>
          <Input
            data-testid={ATID.changePwCurrent}
            type="password"
            value={form.current_password}
            onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))}
            autoComplete="current-password"
            required
          />
        </div>
        <div>
          <Label>Nueva contraseña</Label>
          <Input
            data-testid={ATID.changePwNew}
            type="password"
            value={form.new_password}
            onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
            autoComplete="new-password"
            minLength={8}
            required
          />
          <div className="text-xs text-textgray mt-1">Mínimo 8 caracteres.</div>
        </div>
        <div>
          <Label>Confirmar nueva contraseña</Label>
          <Input
            data-testid={ATID.changePwConfirm}
            type="password"
            value={form.confirm}
            onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>
        <Button
          data-testid={ATID.changePwSubmit}
          type="submit"
          disabled={saving}
          className="w-full bg-nightblue text-white hover:bg-petroleum"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Cambiar contraseña
        </Button>
      </form>
    </div>
  );
}
